<template>
    <Dialog :visible="open" modal dismissableMask :closable="false" :draggable="false"
        contentClass="!p-0 overflow-hidden rounded-lg" :pt="{ header: { class: '!p-0' } }"
        @update:visible="v => !v && $emit('cancel')">

        <!-- HEADER with success/create theme -->
        <template #header>
            <div
                class="flex items-center gap-4 w-full p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-gray-900 rounded-t-lg">
                <div
                    class="flex-shrink-0 w-12 h-12 rounded-full bg-green-500 dark:bg-green-700 flex items-center justify-center">
                    <i class="fa-solid fa-plus text-white text-xl font-bold"></i>
                </div>
                <div class="flex-1 min-w-0">
                    <h4 class="text-xl font-bold">{{ t('createReservation.title') }}</h4>
                </div>
                <Button icon="fa-solid fa-xmark" rounded text severity="secondary" :aria-label="t('common.close')"
                    class="flex-shrink-0" @click="$emit('cancel')" />
            </div>
        </template>

        <!-- BODY -->
        <div class="p-3 space-y-6 max-h-[70vh] overflow-y-auto">

            <!-- Selected Time Slot -->
            <div class="space-y-1">
                <h5 class="font-bold uppercase tracking-wide flex items-center gap-1">
                    <i class="fa-regular fa-clock"></i>
                    <span>{{ t('createReservation.selectedSlot') }}</span>
                </h5>

                <div
                    class="rounded-xl border-2 p-3 border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-gray-900">
                    <div class="flex items-start">
                        <div class="flex-1 space-y-2">
                            <!-- Date -->
                            <div class="flex items-center gap-2">
                                <div
                                    class="w-8 h-8 rounded-lg bg-white/80 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                                    <i class="fa-regular fa-calendar text-green-600"></i>
                                </div>
                                <div>
                                    <p
                                        class="text-green-700 dark:text-emerald-600 font-bold uppercase tracking-wide mb-0">
                                        {{ t('createReservation.date') }}</p>
                                    <p class="font-bold">{{ formatDate(startMs) }}</p>
                                </div>
                            </div>

                            <!-- Time Range -->
                            <div class="flex items-center gap-2">
                                <div
                                    class="w-8 h-8 rounded-lg bg-white/80 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                                    <i class="fa-regular fa-clock text-green-600"></i>
                                </div>
                                <div>
                                    <p class="text-green-700 font-semibold uppercase tracking-wide mb-0">{{
                                        t('createReservation.time') }}</p>
                                    <p class="font-bold">{{ formatTimeRange(startMs, endMs) }}
                                    </p>
                                </div>
                            </div>

                            <!-- Duration -->
                            <div class="flex items-center gap-2">
                                <div
                                    class="w-8 h-8 rounded-lg bg-white/80 dark:bg-emerald-900 flex items-center justify-center flex-shrink-0">
                                    <i class="fa-solid fa-stopwatch text-green-600"></i>
                                </div>
                                <div>
                                    <p class="text-green-700 font-semibold uppercase tracking-wide mb-0">{{
                                        t('createReservation.duration') }}</p>
                                    <p class="font-bold">
                                        {{ calculateDuration(startMs, endMs) }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Resource Selection (Required) -->
            <div class="space-y-1">
                <h5 class="font-bold uppercase tracking-wide flex items-center gap-1">
                    <i class="fa-solid fa-building"></i>
                    <span>{{ t('calendar.new.resourcesSlots.addSlot.resource') }}</span>
                    <span class="text-red-500">*</span>
                </h5>

                <!-- Loading state -->
                <div v-if="loadingAvailability"
                    class="flex items-center gap-3 p-3 rounded-lg border border-surface-200">
                    <i class="fa-solid fa-spinner fa-spin text-primary"></i>
                    <span>{{ t('createReservation.loadingAvailability') }}</span>
                </div>

                <!-- No available resources -->
                <div v-else-if="availableResources.length === 0"
                    class="p-3 rounded-lg border-2 border-orange-200 bg-orange-50">
                    <div class="flex items-center gap-2 text-orange-700">
                        <i class="fa-solid fa-triangle-exclamation"></i>
                        <span>{{ t('createReservation.noAvailableResources') }}</span>
                    </div>
                </div>

                <!-- Resource multiselect -->
                <MultiSelect v-else v-model="selectedResourceIds" :options="availableResources" optionLabel="name"
                    optionValue="id" :placeholder="t('calendar.new.resourcesSlots.selectResource')" class="w-full"
                    filter display="chip" :maxSelectedLabels="3">
                    <template #option="slotProps">
                        <div class="flex flex-col">
                            <span class="font-medium">{{ slotProps.option.name }}</span>
                            <span>{{ slotProps.option.location }}</span>
                        </div>
                    </template>
                </MultiSelect>
            </div>

            <Divider />

            <!-- Next Steps Card -->
            <div
                class="rounded-xl p-2 space-y-2 bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200">
                <div class="flex items-center gap-2">
                    <div class="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                        <i class="fa-solid fa-arrow-right text-white"></i>
                    </div>
                    <h5 class="font-bold text-purple-900 mb-2">{{ t('createReservation.nextSteps') }}</h5>
                </div>
                <div class="ml-2">
                    <ol class="space-y-2 text-purple-800">
                        <li class="flex items-start gap-2">
                            <i class="fa-solid fa-check-circle text-purple-600 mt-0.5 flex-shrink-0"></i>
                            <span>{{ t('createReservation.step1') }}</span>
                        </li>
                        <li class="flex items-start gap-2">
                            <i class="fa-solid fa-check-circle text-purple-600 mt-0.5 flex-shrink-0"></i>
                            <span>{{ t('createReservation.step2') }}</span>
                        </li>
                        <li class="flex items-start gap-2">
                            <i class="fa-solid fa-check-circle text-purple-600 mt-0.5 flex-shrink-0"></i>
                            <span>{{ t('createReservation.step3') }}</span>
                        </li>
                    </ol>
                </div>
            </div>
        </div>

        <!-- FOOTER -->
        <div class="flex justify-end p-3 border-t bg-gray-50">
            <Button :label="t('common.continue')" icon="fa-solid fa-arrow-right" severity="success"
                class="!rounded-lg font-semibold" @click="$emit('confirm', selectedResourceIds)"
                :disabled="selectedResourceIds.length === 0 || loadingAvailability" />
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import { getAvailabilitySlots } from "@/lib/api/availability";
import type { ResourceAvailabilitySlot } from "@/lib/types/availability";
import { useResourcesStore } from "@/store/resource";
import { dayOfWeekMatches, getDayOfWeekFromDate } from "@/utils/date";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import MultiSelect from "primevue/multiselect";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const resourcesStore = useResourcesStore();

const props = defineProps<{
    open: boolean
    startMs: number
    endMs: number
}>()

const emit = defineEmits<{
    (e: "confirm", resourceIds: string[]): void
    (e: "cancel"): void
}>()

const selectedResourceIds = ref<string[]>([]);

// Availability filtering
const availabilityMap = ref<Map<string, ResourceAvailabilitySlot>>(new Map());
const loadingAvailability = ref(false);

function timeStrToMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
}

function msToTimeStr(ms: number): string {
    const d = new Date(ms);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const availableResources = computed(() => {
    // While loading, show nothing (skeleton/spinner is shown instead)
    if (loadingAvailability.value) return [];

    // If no availability data was fetched, show all resources as fallback
    if (availabilityMap.value.size === 0) return resourcesStore.resources;

    const date = new Date(props.startMs);
    const dayName = getDayOfWeekFromDate(date);
    const slotStart = timeStrToMinutes(msToTimeStr(props.startMs));
    const slotEnd = timeStrToMinutes(msToTimeStr(props.endMs));

    return resourcesStore.resources.filter(resource => {
        const availability = availabilityMap.value.get(resource.id);
        if (!availability?.slots?.length) return false;

        return availability.slots
            .filter(slot => dayOfWeekMatches(slot.dayOfWeek, dayName))
            .some(slot =>
                timeStrToMinutes(slot.start) <= slotStart
                && timeStrToMinutes(slot.end) >= slotEnd
            );
    });
});

async function fetchAllAvailabilities() {
    loadingAvailability.value = true;
    const map = new Map<string, ResourceAvailabilitySlot>();

    try {
        const results = await Promise.allSettled(
            resourcesStore.resources.map(r => getAvailabilitySlots(r.id))
        );

        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                map.set(resourcesStore.resources[index].id, result.value);
            }
        });

        availabilityMap.value = map;
    } catch (err) {
        console.error('Error fetching availabilities:', err);
    } finally {
        loadingAvailability.value = false;
    }
}

// Reset selection and fetch availability when dialog opens
watch(() => props.open, async (isOpen) => {
    if (isOpen) {
        selectedResourceIds.value = [];
        await fetchAllAvailabilities();
    }
})

function formatDate(ts: number) {
    const { locale: currentLocale } = useI18n()
    return new Date(ts).toLocaleDateString(currentLocale.value, {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric"
    })
}

function formatTimeRange(start: number, end: number) {
    const { locale: currentLocale } = useI18n()
    const s = new Date(start).toLocaleTimeString(currentLocale.value, { hour: "2-digit", minute: "2-digit" })
    const e = new Date(end).toLocaleTimeString(currentLocale.value, { hour: "2-digit", minute: "2-digit" })
    return `${s} - ${e}`
}

function calculateDuration(start: number, end: number) {
    const diff = end - start
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 0 && minutes > 0) {
        return t('calendar.new.resourcesSlots.addSlot.durationFormat', {
            hours: hours,
            hourUnit: t('time.hour', hours),
            minutes: minutes,
            minuteUnit: t('time.minute', minutes)
        })
    } else if (hours > 0) {
        return `${hours} ${t('time.hour', hours)}`
    } else {
        return `${minutes} ${t('time.minute', minutes)}`
    }
}
</script>
